import axios from 'axios';
import env from '../config/env';
import keycloak from '../config/keycloak';
import { emitForbidden } from './forbiddenEvents';

export const API_URL = env.api.baseUrl;
export const API_BASE_PATH = env.api.basePath;

// Create a shared axios instance with common configuration
const api = axios.create({
  baseURL: `${API_URL}${API_BASE_PATH}`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor for adding the Keycloak token
api.interceptors.request.use(
  async (config) => {
    try {
      // Update token if it's expired
      if (keycloak.token) {
        await keycloak.updateToken(30); // Update token if it expires in 30 seconds
        config.headers['Authorization'] = `Bearer ${keycloak.token}`;
      }
      return config;
    } catch (error) {
      console.error('Failed to update Keycloak token:', error);
      keycloak.login(); // Redirect to login if token update fails
      return Promise.reject(error);
    }
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Handler degli errori di risposta, estratto dall'interceptor per essere testabile
 * senza una richiesta HTTP reale.
 *
 * Il 403 non redirige al login: l'utente è autenticato, gli manca un ruolo. Rifare il
 * login gli restituirebbe lo stesso token e lo stesso 403. Viene invece notificato alla
 * UI, perché senza questo ramo una negazione di autorizzazione arriva all'utente come
 * un messaggio di errore generico del componente — indistinguibile da un guasto di rete.
 */
export const handleResponseError = (error) => {
  const status = error.response && error.response.status;

  // 401 Unauthorized: token assente, scaduto o non valido → login
  if (status === 401) {
    keycloak.login();
  }

  // 403 Forbidden: autenticato ma senza il ruolo richiesto
  if (status === 403) {
    const method = (error.config && error.config.method ? error.config.method : 'get').toUpperCase();
    const url = `${(error.config && error.config.baseURL) || ''}${(error.config && error.config.url) || ''}`;

    console.warn(`403 Forbidden: ${method} ${url}`);
    error.isForbidden = true;

    // Solo le mutazioni vengono notificate all'utente. Una lettura negata lascia una
    // sezione vuota, che in una UI a ruoli è il comportamento atteso e non un guasto:
    // notificarla produce un falso allarme a ogni pagina per i ruoli che non hanno
    // quella lettura (26 dei 46 GET della matrice sono negati a un viewer, e la fetch
    // delle code in Layout.js parte per chiunque). Una mutazione negata invece nasce
    // sempre da un'azione esplicita: senza avviso l'utente clicca e non accade nulla.
    // La diagnosi non dipende da questo avviso — ErrorLoggingFilter registra ogni 403
    // lato backend con l'identità dell'utente e il clientId.
    if (method !== 'GET') {
      emitForbidden({ method, url });
    }
  }

  // Note: Storage errors (503) are now handled locally in each component
  // They will display error messages in the UI where appropriate

  return Promise.reject(error);
};

// Add response interceptor to handle common errors
api.interceptors.response.use(
  response => response,
  handleResponseError
);

export default api;
